import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from "react";
import {
  DocumentEditorContainerComponent,
  Ribbon
} from "@syncfusion/ej2-react-documenteditor";
import { Box, CircularProgress } from "@mui/material";

DocumentEditorContainerComponent.Inject(Ribbon);

const SyncfusionWordEditor = forwardRef(
({ fileId, reloadKey, onChange }, ref) => {
  const editorRef = useRef(null);
  const loadedRef = useRef(false);
  const [loading, setLoading] = useState(true);

  /* -------- LOAD DOCUMENT -------- */
  const loadDocument = async () => {
    const editor = editorRef.current?.documentEditor;
    if (!editor) return;

    setLoading(true);

    try {
      const res = await fetch(
        `https://yaazhlegal.org.in/documenteditor/open-by-fileid/${fileId}`,
        { method: "POST" }
      );

      const sfdt = await res.text();
      editor.open(sfdt || "{}");

      requestAnimationFrame(() => editor.resize());
    } catch (e) {
      console.error("Document load failed", e);
    } finally {
      setLoading(false);
    }
  };

  /* -------- INITIAL LOAD -------- */
  const handleCreated = async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    await loadDocument();
  };

  /* -------- RELOAD (TAB-4) -------- */
  useEffect(() => {
    if (!loadedRef.current) return;
    loadDocument();
  }, [reloadKey]);

  /* -------- CONTENT CHANGE -------- */
  const handleContentChange = () => {
    const editor = editorRef.current?.documentEditor;
    if (editor && onChange && !loading) {
      onChange(editor.serialize());
    }
  };

    /* -------- EXPOSE METHODS -------- */
    useImperativeHandle(ref, () => ({
      resizeEditor() {
        editorRef.current?.documentEditor?.resize();
      },

      async exportDocx(filename = `${fileId}_Report.docx`) {
        const editor = editorRef.current?.documentEditor;
        if (!editor) return;

        const blob = await editor.saveAsBlob("Docx");
        downloadBlob(blob, filename);
      },

      printPdf(filename = `YLA_${fileId}_Legal_Report`) {
        const editor = editorRef.current?.documentEditor;
        if (!editor) return;

        // 🔹 Save original title
        const originalTitle = document.title;

        // 🔹 Set PDF name
        document.title = filename;

        // 🔹 Legal page setup
        editor.pageSetup = {
          pageWidth: 612,     // Legal width (8.5")
          pageHeight: 1008,   // Legal height (14")
          orientation: 'Portrait',
          topMargin: 72,
          bottomMargin: 72,
          leftMargin: 72,
          rightMargin: 72,
        };

        // 🔹 Print
        editor.print();

        // 🔹 Restore title (important)
        setTimeout(() => {
          document.title = originalTitle;
        }, 1000);
      },

      async exportPdf(fileId, fileName) {
        console.log('File name:' + `YLA_Legal_Report_${fileId}_${fileName}.pdf`);
        const sfdt = editorRef.current.documentEditor.serialize();

        const res = await fetch(
          "https://yaazhlegal.org.in/documenteditor/Export",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileName: `YLA_${fileId}_Legal_Report.pdf`,
              documentData: sfdt
            })
          }
        );

        const blob = await res.blob();
        downloadBlob(blob, `YLA_Legal_Report_${fileId}_${fileName}.pdf`);
      },

      isLoading: loading
    }));

    function downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      {/* 🔩ვენ EDITOR */}
      <DocumentEditorContainerComponent
        ref={editorRef}
        height="100%"
        width="100%"
        enableToolbar={true}
        toolbarMode="Ribbon"
        enableSpellCheck={true}
        locale="en-GB"
        serviceUrl="https://yaazhlegal.org.in/documenteditor/"
        created={handleCreated}
        contentChange={handleContentChange}
      />

      {/* 🔄 LOADER OVERLAY */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress size={50} />
        </Box>
      )}
    </Box>
  );
});

export default SyncfusionWordEditor;
